import React from "react";

import { Banner, Button, Toast } from "@newsela/angelou";
import {
  actionNames,
  actionPrefixes,
  objectTypes,
  withTrackEvent,
} from "@newsela/snitch";
import { object, func } from "prop-types";

import {
  contentSettingsButtonGroupCss,
  contentSettingsCancelButtonCss,
  contentSettingsConfirmationContainerCss,
} from "@/admin/AdminContentSettingsSearchFormOverlay/AdminContentSettingsSearchFormOverlay.style";
import useContentItemVisibilityPost from "@/admin/hooks/useContentItemVisibilityPost";
import globalToasterRef from "@/base/globalToasterRef";

const ButtonWithTrackEvent = withTrackEvent(Button);

const propTypes = {
  reset: func.isRequired,
  trigger: func.isRequired,
  handleSubmit: func.isRequired,
  articleChanges: object.isRequired,
};

const AdminContentSettingsSearchFormBanner = ({
  reset,
  trigger,
  handleSubmit,
  articleChanges,
}) => {
  // initialize the Request to submit the Content Selections
  const [postContentItemSelections] = useContentItemVisibilityPost();

  const postToContentItemVisibility = async (visiblityData, formData) => {
    try {
      const response = await postContentItemSelections(visiblityData);
      if (response.error) {
        // TODO: extract toasts to types
        globalToasterRef.current.displayToast({
          showIcon: true,
          statusColor: Toast.statusColor.danger,
          children: response.error.message,
        });
        return;
      }
      if (response.data) {
        // TODO: extract to toast types
        globalToasterRef.current.displayToast({
          children: "Changes saved.",
          statusColor: Toast.statusColor.success,
        });
        // clean the form, but not pristine
        // keep their values to hide the viz marker cache TTL
        reset({ ...formData });
      }
    } catch {
      // TODO: extract toasts to types
      globalToasterRef.current.displayToast({
        showIcon: true,
        statusColor: Toast.statusColor.danger,
        children:
          "There was an error saving your selections. Please try again.",
      });
    }
  };

  // Submission callback
  // react-hook-form `handleSubmit` will use this
  const onSubmit = (data) => {
    postToContentItemVisibility(articleChanges, data);
  };

  const articleChangesCount = Object.keys(articleChanges).length;

  return (
    <div css={contentSettingsConfirmationContainerCss}>
      <Banner
        bannerType={Banner.types.info}
        __renderBody={() => (
          <p role="alert">
            {`You made changes to ${articleChangesCount} ${articleChangesCount === 1 ? " article" : " articles"}`}
          </p>
        )}
        __renderCtaSection={() => {
          return (
            <div css={contentSettingsButtonGroupCss}>
              <ButtonWithTrackEvent
                actionName={actionNames.cancel}
                actionPrefix={actionPrefixes.initiate}
                objectTypes={objectTypes.user}
                onClick={() => reset()}
                __cssFor={{ root: contentSettingsCancelButtonCss }}
                streamProperties={{
                  description:
                    "Admin Content Settings cancel flagged content changes",
                }}
              >
                Cancel
              </ButtonWithTrackEvent>

              <ButtonWithTrackEvent
                actionName={actionNames.save}
                actionPrefix={actionPrefixes.initiate}
                objectTypes={objectTypes.user}
                onClick={() => {
                  // manually trigger react-hook-form submission
                  trigger().then((isValid) => {
                    if (isValid) handleSubmit(onSubmit)();
                  });
                }}
                flavor={Button.flavors.muted}
                streamProperties={{
                  description:
                    "Admin Content Settings save flagged content changes",
                }}
              >
                Save changes
              </ButtonWithTrackEvent>
            </div>
          );
        }}
        customConfig={{
          SvgComponent: null,
          colors: {
            primary: "#04337A",
            foregroundProfile: Banner.foregroundProfiles.light,
          },
        }}
      />
    </div>
  );
};

AdminContentSettingsSearchFormBanner.propTypes = propTypes;
AdminContentSettingsSearchFormBanner.displayName =
  "AdminContentSettingsSearchFormBanner";
export { AdminContentSettingsSearchFormBanner };
